import * as THREE from 'three';
export function savePlyFile(host, fileIndex) {
    try {
        if (fileIndex < 0 || fileIndex >= host.spatialFiles.length) {
            throw new Error('Invalid file index');
        }
        const spatialData = host.spatialFiles[fileIndex];
        host.showStatus(`Generating PLY file for ${spatialData.fileName}...`);
        // Generate PLY file content with current state (including transformations and colors)
        const plyContent = generatePlyFileContent(host, spatialData, fileIndex);
        // Use VS Code save dialog instead of automatic download
        const defaultFileName = spatialData.fileName || `pointcloud_${fileIndex + 1}.ply`;
        host.vscode.postMessage({
            type: 'savePlyFile',
            content: plyContent,
            defaultFileName: defaultFileName,
            fileIndex: fileIndex,
        });
        host.showStatus(`Opening save dialog for ${defaultFileName}...`);
    }
    catch (error) {
        console.error('Error preparing PLY file:', error);
        host.showError(`Failed to prepare PLY file: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export function generatePlyFileContent(host, spatialData, fileIndex) {
    // Get current transformed vertices from the actual geometry
    const mesh = host.meshes[fileIndex];
    const geometry = mesh.geometry;
    const positionAttribute = geometry.getAttribute('position');
    const colorAttribute = geometry.getAttribute('color');
    mesh.updateWorldMatrix(true, false);
    const worldPoint = new THREE.Vector3();
    const sourceOrigin = spatialData.sourceOrigin ?? [0, 0, 0];
    const vertexCount = positionAttribute.count;
    // PLY header
    let content = 'ply\n';
    content += `format ascii 1.0\n`;
    // Add comments including transformation info
    content += `comment Generated from ${spatialData.fileName || 'point cloud'}\n`;
    content += `comment Coordinate system: OpenGL (Y-up, Z-backward)\n`;
    if (spatialData.comments.length > 0) {
        spatialData.comments.forEach(comment => {
            content += `comment ${comment}\n`;
        });
    }
    // Vertex element definition
    content += `element vertex ${vertexCount}\n`;
    content += 'property float x\n';
    content += 'property float y\n';
    content += 'property float z\n';
    const hasColors = !!colorAttribute;
    if (hasColors) {
        content += 'property uchar red\n';
        content += 'property uchar green\n';
        content += 'property uchar blue\n';
    }
    if (spatialData.hasNormals) {
        content += 'property float nx\n';
        content += 'property float ny\n';
        content += 'property float nz\n';
    }
    // Face element definition (if any)
    if (spatialData.faceCount > 0) {
        content += `element face ${spatialData.faceCount}\n`;
        content += 'property list uchar int vertex_indices\n';
    }
    content += 'end_header\n';
    // Point-cloud colors are stored as raw 8-bit sRGB (Uint8); mesh/intensity
    // colors as Float32 [0,1]. Scale accordingly so export matches what's shown.
    const colorIsByte = hasColors &&
        (colorAttribute.array instanceof Uint8Array ||
            colorAttribute.array instanceof Uint8ClampedArray);
    const colorScale = colorIsByte ? 1 : 255;
    // Export through matrixWorld so the editable per-file transform is reflected
    // in the saved coordinates, as promised by the save action.
    for (let i = 0; i < vertexCount; i++) {
        const i3 = i * 3;
        worldPoint.fromBufferAttribute(positionAttribute, i).applyMatrix4(mesh.matrixWorld);
        // LiDAR imports are rendered near the origin for float32 GPU precision.
        // Restore the high-precision source-space origin on export after applying
        // the visible per-file transform to the local cloud.
        worldPoint.x += sourceOrigin[0];
        worldPoint.y += sourceOrigin[1];
        worldPoint.z += sourceOrigin[2];
        const { x, y, z } = worldPoint;
        content += `${x} ${y} ${z}`;
        if (hasColors) {
            const r = Math.round(colorAttribute.array[i3] * colorScale);
            const g = Math.round(colorAttribute.array[i3 + 1] * colorScale);
            const b = Math.round(colorAttribute.array[i3 + 2] * colorScale);
            content += ` ${r} ${g} ${b}`;
        }
        if (spatialData.hasNormals && spatialData.vertices[i]) {
            const vertex = spatialData.vertices[i];
            content += ` ${vertex.nx || 0} ${vertex.ny || 0} ${vertex.nz || 0}`;
        }
        content += '\n';
    }
    // Face data (if any) - these don't change with transformations
    spatialData.faces.forEach(face => {
        content += `${face.indices.length}`;
        face.indices.forEach(index => {
            content += ` ${index}`;
        });
        content += '\n';
    });
    return content;
}
